import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetFormFields } from "@/hooks";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { useCallback, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormAutocomplete } from "./form-autocomplete";
import { AggregationType, FormData } from "@/types/dashboard.types";
import { TrendingUp, Hash, Grid, Plus, X } from "lucide-react";

interface CCTFactor {
  fieldId: string;
  label?: string;
}

interface CCTMeasure {
  id?: string;
  fieldId: string;
  aggregation: AggregationType;
  label?: string;
}

interface CCTConfig {
  formId: string;
  factors: CCTFactor[];
  measures: CCTMeasure[];
}

interface CctMetricEditorProps {
  config: CCTConfig;
  onChange: (config: CCTConfig) => void;
  availableForms: FormData[];
}

function FactorRow({
  factor,
  index,
  formId,
  onChange,
  onRemove,
}: {
  factor: CCTFactor;
  index: number;
  formId: string;
  onChange: (factor: CCTFactor) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { data: formFieldsData, isLoading } = useGetFormFields(formId);

  const availableFields = useMemo(() => {
    if (!formId || !formFieldsData) return [];

    const systemFields = [
      { id: "$responseId$", label: "Response ID", type: "categorical" },
      { id: "$submissionDate$", label: "Submission Date", type: "datetime" },
    ];

    const formFields = formFieldsData?.formFields || [];
    return [...systemFields, ...formFields];
  }, [formId, formFieldsData]);

  const selectedField = availableFields.find(f => f.id === factor.fieldId);

  return (
    <div className="flex items-end gap-2 p-3 border rounded-lg">
      <div className="flex-1 space-y-2">
        <Label>{t('processManagement.factor')} {index + 1}</Label>
        <Select
          value={factor.fieldId || ""}
          onValueChange={(fieldId) => onChange({ ...factor, fieldId })}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('processManagement.selectField')} />
          </SelectTrigger>
          <SelectContent>
            {availableFields.map((field) => (
              <SelectItem key={field.id} value={field.id!}>
                {field.id!.startsWith("$") ? (
                  <div className="flex items-center gap-2">
                    <span className="text-blue-600">📊</span>
                    {field.label} ({t('processManagement.system')})
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    {field.label} ({field.type})
                  </div>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1 space-y-2">
        <Label>{t('processManagement.label')} ({t('processManagement.optional')})</Label>
        <Input
          value={factor.label || ''}
          onChange={(e) => onChange({ ...factor, label: e.target.value })}
          placeholder={selectedField?.label || t('processManagement.customLabel')}
        />
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onRemove}
        className="mb-0"
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}

function MeasureRow({
  measure,
  index,
  formId,
  onChange,
  onRemove,
}: {
  measure: CCTMeasure;
  index: number;
  formId: string;
  onChange: (measure: CCTMeasure) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { data: formFieldsData, isLoading } = useGetFormFields(formId);

  const availableFields = useMemo(() => {
    if (!formId || !formFieldsData) return [];
    const formFields = formFieldsData?.formFields || [];
    return formFields.filter((f: any) => f.type === 'number' || f.type === 'currency' || f.type === 'decimal' || f.type === 'integer');
  }, [formId, formFieldsData]);

  const selectedField = availableFields.find((f: any) => f.id === measure.fieldId);

  const availableAggregations: AggregationType[] = [
    "count", "sum", "mean", "median", "mode", "min", "max", "std", "variance", "p10", "p25", "p50", "p75", "p90"
  ];

  return (
    <div className="flex items-end gap-2 p-3 border rounded-lg">
      <div className="flex-1 space-y-2">
        <Label>{t('processManagement.measure')} {index + 1}</Label>
        <Select
          value={measure.fieldId || ""}
          onValueChange={(fieldId) => onChange({ ...measure, fieldId })}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('processManagement.selectNumericField')} />
          </SelectTrigger>
          <SelectContent>
            {availableFields.map((field: any) => (
              <SelectItem key={field.id} value={field.id!}>
                <div className="flex items-center gap-2">
                  {field.label} ({field.type})
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1 space-y-2">
        <Label>{t('processManagement.aggregation')}</Label>
        <Select
          value={measure.aggregation || ""}
          onValueChange={(aggregation: AggregationType) => onChange({ ...measure, aggregation })}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('processManagement.selectAggregation')} />
          </SelectTrigger>
          <SelectContent>
            {availableAggregations.map((agg) => (
              <SelectItem key={agg} value={agg}>
                {agg.charAt(0).toUpperCase() + agg.slice(1)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex-1 space-y-2">
        <Label>{t('processManagement.label')} ({t('processManagement.optional')})</Label>
        <Input
          value={measure.label || ''}
          onChange={(e) => onChange({ ...measure, label: e.target.value })}
          placeholder={`${selectedField?.label || 'field'} ${measure.aggregation}`}
        />
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onRemove}
        className="mb-0"
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}

export function CctMetricEditor({
  config,
  onChange,
  availableForms,
}: CctMetricEditorProps) {
  const { t } = useTranslation();

  const updateConfig = useCallback((updates: Partial<CCTConfig>) => {
    const newConfig = { ...config, ...updates };
    onChange(newConfig);
  }, [config, onChange]);

  const addFactor = useCallback(() => {
    updateConfig({
      factors: [...config.factors, { fieldId: '' }]
    });
  }, [config.factors, updateConfig]);

  const updateFactor = useCallback((index: number, factor: CCTFactor) => {
    const newFactors = [...config.factors];
    newFactors[index] = factor;
    updateConfig({ factors: newFactors });
  }, [config.factors, updateConfig]);

  const removeFactor = useCallback((index: number) => {
    const newFactors = config.factors.filter((_, i) => i !== index);
    updateConfig({ factors: newFactors });
  }, [config.factors, updateConfig]);

  const addMeasure = useCallback(() => {
    updateConfig({
      measures: [...config.measures, { fieldId: '', aggregation: 'sum' }]
    });
  }, [config.measures, updateConfig]);

  const updateMeasure = useCallback((index: number, measure: CCTMeasure) => {
    const newMeasures = [...config.measures];
    newMeasures[index] = measure;
    updateConfig({ measures: newMeasures });
  }, [config.measures, updateConfig]);

  const removeMeasure = useCallback((index: number) => {
    const newMeasures = config.measures.filter((_, i) => i !== index);
    updateConfig({ measures: newMeasures });
  }, [config.measures, updateConfig]);

  const { data: formFieldsData } = useGetFormFields(config.formId);
  const formFields = formFieldsData?.formFields || [];

  const previewText = useMemo(() => {
    if (!config.factors.length || !config.measures.length) return t('processManagement.configureFactorsAndMeasures');

    const factorLabels = config.factors.map((f, i) => {
      const field = formFields.find((ff: any) => ff.id === f.fieldId);
      return f.label || field?.label || `Factor ${i + 1}`;
    }).join(' × ');

    const measureLabels = config.measures.map((m, i) => {
      const field = formFields.find((ff: any) => ff.id === m.fieldId);
      const label = m.label || `${field?.label || 'field'} ${m.aggregation}`;
      return `${m.aggregation} of ${label}`;
    }).join(', ');

    return `${measureLabels} by ${factorLabels}`;
  }, [config.factors, config.measures, formFields, t]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Grid className="w-5 h-5" />
          {t('processManagement.cartesianCombinationTableConfiguration')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Form Selection */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Hash className="w-4 h-4" />
            <Label className="text-sm font-medium">{t('processManagement.dataSource')}</Label>
          </div>

          <div className="space-y-2">
            <Label>{t('processManagement.form')}</Label>
            <FormAutocomplete
              key={`cct-form-${config.formId}`}
              value={config.formId}
              onValueChange={(formId) => {
                updateConfig({
                  formId: formId || '',
                  factors: [],
                  measures: [],
                });
              }}
              forms={availableForms.map(f => ({
                id: f.id,
                title: f.title,
                name: f.name || f.title || "",
                description: f.description,
              }))}
              placeholder={t('processManagement.searchForms')}
            />
          </div>
        </div>

        <div className="border-t-4 border-gray-200 my-6" />

        {/* Factors */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Hash className="w-4 h-4" />
              <Label className="text-sm font-medium">{t('processManagement.factors')}</Label>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addFactor}>
              <Plus className="w-4 h-4 mr-2" />
              {t('processManagement.addFactor')}
            </Button>
          </div>

          <div className="space-y-3">
            {config.factors.map((factor, index) => (
              <FactorRow
                key={index}
                factor={factor}
                index={index}
                formId={config.formId}
                onChange={(updatedFactor) => updateFactor(index, updatedFactor)}
                onRemove={() => removeFactor(index)}
              />
            ))}
            {config.factors.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {t('processManagement.noFactorsAdded')}
              </div>
            )}
          </div>
        </div>

        <div className="border-t-4 border-gray-200 my-6" />

        {/* Measures */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              <Label className="text-sm font-medium">{t('processManagement.measures')}</Label>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={addMeasure}>
              <Plus className="w-4 h-4 mr-2" />
              {t('processManagement.addMeasure')}
            </Button>
          </div>

          <div className="space-y-3">
            {config.measures.map((measure, index) => (
              <MeasureRow
                key={index}
                measure={measure}
                index={index}
                formId={config.formId}
                onChange={(updatedMeasure) => updateMeasure(index, updatedMeasure)}
                onRemove={() => removeMeasure(index)}
              />
            ))}
            {config.measures.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {t('processManagement.noMeasuresAdded')}
              </div>
            )}
          </div>
        </div>

        {/* Info */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>{t('processManagement.note')}:</strong> {t('processManagement.cctGeneratesAllCombinations')}
          </p>
        </div>

        {/* Preview */}
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            <strong>{t('processManagement.preview')}:</strong> {previewText}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
